import type { NpoVideoPlayer, MediaType, PlayerEventNames } from '@npo/player';
import type { NpoVideoPlayerUIFactory } from '@npo/player-video-ui';

export type NpoPlayerModule = {
  NpoVideoPlayer: typeof NpoVideoPlayer;
  MediaType: typeof MediaType;
  PlayerEventNames: typeof PlayerEventNames;
};

export type NpoPlayerUiModule = {
  NpoVideoPlayerUIFactory: typeof NpoVideoPlayerUIFactory;
};

declare global {
  interface Window {
    NpoPlayerModule?: NpoPlayerModule;
    NpoPlayerUiModule?: NpoPlayerUiModule;
  }
}

export default defineNuxtPlugin(() => {
  let loadNpoPlayerPromise: Promise<NpoPlayerModule | undefined> | null = null;
  let loadNpoPlayerUiPromise: Promise<NpoPlayerUiModule | undefined> | null = null;

  return {
    provide: {
      loadNpoPlayer: (): Promise<NpoPlayerModule | undefined> => {
        if (loadNpoPlayerPromise) {
          return loadNpoPlayerPromise;
        }

        loadNpoPlayerPromise = (async () => {
          if (window.NpoPlayerModule) {
            return window.NpoPlayerModule;
          }

          try {
            const data = await import('@npo/player');
            window.NpoPlayerModule = {
              NpoVideoPlayer: data.NpoVideoPlayer,
              MediaType: data.MediaType,
              PlayerEventNames: data.PlayerEventNames,
            };
            console.info(`√ Loaded NPO Player.`);
            return window.NpoPlayerModule;
          } catch (err) {
            const errorMessage = `Failed to load NPO Player: ${err.message}`;
            console.error(errorMessage);
            throw new Error(errorMessage);
          }
        })();

        return loadNpoPlayerPromise;
      },
      loadNpoPlayerUi: (): Promise<NpoPlayerUiModule | undefined> => {
        if (loadNpoPlayerUiPromise) {
          return loadNpoPlayerUiPromise;
        }

        loadNpoPlayerUiPromise = (async () => {
          if (window.NpoPlayerUiModule) {
            return window.NpoPlayerUiModule;
          }

          try {
            const data = await import('@npo/player-video-ui');
            window.NpoPlayerUiModule = {
              NpoVideoPlayerUIFactory: data.NpoVideoPlayerUIFactory,
            };
            return window.NpoPlayerUiModule;
          } catch (err) {
            const errorMessage = `Failed to load NPO Player UI: ${err.message}`;
            console.error(errorMessage);
            throw new Error(errorMessage);
          }
        })();

        return loadNpoPlayerUiPromise;
      },
    },
  };
});
